import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Divider from '@mui/material/Divider';
import { IconButton } from '../../components/IconButton';
import MenuIcon from '@mui/icons-material/Menu';

import { useNavbar } from './useNavbar';
import { NavbarNotification } from './NavbarNotification';
import { UserMenu } from './UserMenu';
import { AccountSelector } from './AccountSelector';
import { LanguageMenu } from './LanguageMenu';
import { NAVBAR_ICON_SIZE } from './navbar-icon-size';
import { useIsMobile } from '../useIsMobile';
import { toggleMobileSidebar } from '../mobile-sidebar';

// Altura do separador vertical: a da linha de controles do legado, onde o divisor é o
// `border-left` de um `.nav-link` de 24px — e não a altura cheia da faixa, que é o que
// o `flexItem` do MUI produziria.
const DIVIDER_HEIGHT = 24;

/**
 * Barra superior que ocupa o slot de navbar da casca de layout. Apenas
 * composição: o estado inteiro e as decisões vêm do `useNavbar`. Os três controles
 * (conta, idioma, usuário) ficam alinhados à direita, separados por divisores
 * verticais; o logo pertence ao menu lateral, não à barra.
 */
export function Navbar(): JSX.Element {
  const {
    personName,
    personPhoto,
    accountName,
    showAccountSelector,
    accounts,
    filter,
    setFilter,
    showMyData,
    showSettings,
    showSelectAccount,
    showChangePassword,
    locales,
    notification,
    onSelectAccount,
    onLeaveAccount,
    onLogout,
    onNavigate,
    onChangeLanguage,
    onDismissNotification,
  } = useNavbar();

  const isMobile = useIsMobile();

  return (
    <AppBar
      position="static"
      color="transparent"
      elevation={0}
      // Fundo transparente e sem elevacao fazem os controles "flutuarem" sobre o conteudo da aplicacao.
      // A cor de ícones e textos sai daqui: todos os controles são `color="inherit"`, e
      // `text.secondary` é o rgba(0,0,0,.6) do `.nav-link`/`i` do legado. Sem isso a
      // herança vem do `body` do SCSS legado, em rgba(0,0,0,.87).
      sx={{
        backgroundColor: 'transparent',
        boxShadow: 'none',
        borderBottom: 'none',
        color: 'text.secondary',
      }}
    >
      {/* Sem gutters: o recuo lateral da faixa é da coluna do shell, não da Toolbar
          (o default do MUI somaria 24px aos 16px da coluna).
          Sem gap: os 8px entre ícone e separador já vêm do padding do IconButton, como
          no legado, onde o espaço até o `border-left` é o padding do `.nav-link`. Um
          gap aqui somaria ao padding e dobraria essa distância. */}
      <Toolbar
        disableGutters
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0 }}
      >
        {isMobile && (
          <IconButton
            onClick={toggleMobileSidebar}
            aria-label="Abrir menu"
            color="inherit"
            sx={{ mr: 'auto' }}
          >
            <MenuIcon sx={{ fontSize: NAVBAR_ICON_SIZE }} />
          </IconButton>
        )}

        <AccountSelector
          showAccountSelector={showAccountSelector}
          accounts={accounts}
          filter={filter}
          setFilter={setFilter}
          accountName={accountName}
          onSelectAccount={onSelectAccount}
          onLeaveAccount={onLeaveAccount}
        />

        <Divider orientation="vertical" sx={{ height: DIVIDER_HEIGHT }} />

        <LanguageMenu locales={locales} onChangeLanguage={onChangeLanguage} />

        <Divider orientation="vertical" sx={{ height: DIVIDER_HEIGHT }} />

        <UserMenu
          personName={personName}
          personPhoto={personPhoto}
          showMyData={showMyData}
          showSettings={showSettings}
          showSelectAccount={showSelectAccount}
          showChangePassword={showChangePassword}
          onNavigate={onNavigate}
          onLogout={onLogout}
        />
      </Toolbar>

      <NavbarNotification notification={notification} onDismiss={onDismissNotification} />
    </AppBar>
  );
}
